import { supabase } from '../utils/supabase';

export interface UserProfile {
  id: string;
  full_name: string;
  avatar_url?: string;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
}

export interface ApiKey {
  id: string;
  user_id: string;
  key: string;
  is_active: boolean;
  created_at: string;
  project_id: string; 
}

export interface UsageLog {
  id: string;
  user_id: string | null;
  api_key: string | null;
  created_at: string;
  action: any | null;
  response: any | null;
  context: any | null;
  project_id: string | null;
  status: "success" | "error"; 
  error_message: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}


export const apiService = {
  fetchOverviewData: async (userId: string, projectId?: string) => {
    try {
      
      const logs = await getUsageLogs(userId, projectId);
      
      
      return {
        calls: logs.length,
        tokens: logs.reduce((acc, log) => acc + (log.input_tokens || 0) + (log.output_tokens || 0), 0),
        activity: logs.map(log => ({ 
          date: log.created_at, 
          value: (log.input_tokens || 0) + (log.output_tokens || 0)
        })),
      };
    } catch (error) {
      
      return { calls: 0, tokens: 0, activity: [] };
    }
  },
  regenerateApiKey: async (userId: string, projectId: string) => {
    const oldKey = await getApiKey(userId, projectId);
    if (oldKey) await deactivateApiKey(oldKey.id);
    return createApiKey(userId, crypto.randomUUID(), projectId);
  },
  createProjectAndKey: async (userId: string, projectName: string) => {
    const project = await createProject(userId, projectName);
    
    
    if (project && project.id) {
      await createApiKey(userId, crypto.randomUUID(), project.id);
      
      return project;
    } else {
      
      throw new Error('Failed to create project');
    }
  },
  
  getAllProjects: async (userId: string) => {
    try {
      return await getUserProjects(userId);
    } catch (error) {
      
      return [];
    }
  },
  getProjectAnalytics: async (projectId: string) => {
    const logs = await getProjectLogs(projectId);
    return {
      calls: logs.length,
      inputTokens: logs.reduce((acc, log) => acc + (log.input_tokens || 0), 0),
      outputTokens: logs.reduce((acc, log) => acc + (log.output_tokens || 0), 0),
      dailyUsage: aggregateDailyUsage(logs),
      topActions: getTopActions(logs)
    };
  }
};


export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    
    return null;
  }
};

export const createUserProfile = async (userId: string, fullName: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .insert({ id: userId, full_name: fullName })
      .maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    
    return null;
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<UserProfile>): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    
    return null;
  }
};

export const getUserProjects = async (userId: string): Promise<Project[]> => {
  try {

    if (!userId) {
      
      return [];
    }
    
    
    
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      
      throw error;
    }
    
    
    if (data && data.length > 0) {
      
    } else {
      
    }
    
    return data || [];
  } catch (error) {
    
    return [];
  }
};

export const getProjectById = async (projectId: string): Promise<Project | null> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    
    return null;
  }
};

export const createProject = async (userId: string, name: string): Promise<Project | null> => {
  try {
    
    

    const { data, error } = await supabase
      .from('projects')
      .insert({ 
        user_id: userId,
        name 
      })
      .select()  // Add this line to ensure Supabase returns the inserted data
      .maybeSingle();

    if (error) {
      
      throw error;
    }
    
    
    return data;
  } catch (error) {
    
    return null;
  }
};

export const updateProjectName = async (projectId: string, name: string): Promise<Project | null> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({ name })
      .eq('id', projectId)
      .maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    
    return null;
  }
};

export const deleteProjectById = async (projectId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
    return true;
  } catch (error) {
    
    return false;
  }
};

export const getProjectLogs = async (projectId: string): Promise<UsageLog[]> => {
  try {
    const { data, error } = await supabase
      .from('usage_logs')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    
    return [];
  }
};

export const getApiKey = async (userId: string, projectId?: string): Promise<ApiKey | null> => {
  try {
    let query = supabase
      .from('api_keys')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });
      
    if (projectId) {
      query = query.eq('project_id', projectId);
    }
    
    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    
    return null;
  }
};

export async function createApiKey(
  userId: string,
  key: string,
  projectId: string
): Promise<ApiKey> {
  const { data, error } = await supabase
    .from('api_keys')
    .insert({ user_id: userId, key, project_id: projectId })
    .single()

  if (error) {
    console.error('createApiKey error:', error)
    throw error
  }

  return data
}


export const deactivateApiKey = async (apiKeyId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('api_keys')
      .update({ is_active: false })
      .eq('id', apiKeyId);

    if (error) throw error;
    return true;
  } catch (error) {
    
    return false;
  }
};

export const getUsageLogs = async (userId: string, projectId?: string): Promise<UsageLog[]> => {
  try {
    let query = supabase
      .from('usage_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      
    if (projectId) {
      query = query.eq('project_id', projectId);
    }
    
    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    
    return [];
  }
};

export const insertUsageLog = async (log: Omit<UsageLog, 'id' | 'created_at'>): Promise<UsageLog | null> => {
  try {
    const { data, error } = await supabase
      .from('usage_logs')
      .insert(log)
      .maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    
    return null;
  }
};

const aggregateDailyUsage = (logs: UsageLog[]): {date: string, value: number}[] => {
  const dailyUsage: Record<string, number> = {};
  
  logs.forEach(log => {
    const date = new Date(log.created_at).toLocaleDateString();
    const totalTokens = (log.input_tokens || 0) + (log.output_tokens || 0);
    
    if (!dailyUsage[date]) {
      dailyUsage[date] = 0;
    }
    
    dailyUsage[date] += totalTokens;
  });
  
  return Object.entries(dailyUsage).map(([date, value]) => ({ date, value }));
};

const getTopActions = (logs: UsageLog[]): {action: string, count: number}[] => {
  const actionCounts: Record<string, number> = {};
  
  logs.forEach(log => {
    let actionName = 'unknown';
    
    if (typeof log.action === 'string') {
      actionName = log.action;
    } else if (log.action && typeof log.action === 'object') {
      actionName = JSON.stringify(log.action).substring(0, 30);
    }
    
    if (!actionCounts[actionName]) {
      actionCounts[actionName] = 0;
    }
    
    actionCounts[actionName]++;
  });
  
  return Object.entries(actionCounts)
    .map(([action, count]) => ({ action, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
};